import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, ShieldAlert, Eye, EyeOff, Gauge, Bell, TrendingDown, TrendingUp, Minus, KeyRound, Link2, CalendarClock, FileWarning, Users, Scale, Landmark } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getCaseCreditorWatch, refreshCaseCreditorWatch, watchCaseCreditorWatch, unwatchCaseCreditorWatch, pullCaseCreditorWatchReport, linkAsicAbr } from "@/lib/api";

const errMsg = (e, fb) => e?.response?.data?.detail || fb;
const fmtAbn = (a) => (a ? a.replace(/^(\d{2})(\d{3})(\d{3})(\d{3})$/, "$1 $2 $3 $4") : "—");
const fmtDT = (s) => (s ? new Date(s).toLocaleString("en-AU", { timeZone: "Australia/Sydney", dateStyle: "medium", timeStyle: "short" }) : "—");
const fmtD = (s) => (s ? new Date(`${s.slice(0, 10)}T12:00:00`).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

// Rating letter → colour family (A best … F worst). Score bands per CW docs.
export const ratingCls = (r) => {
  const l = (r || "").charAt(0).toUpperCase();
  return { A: "bg-emerald-50 text-emerald-700 border-emerald-300", B: "bg-lime-50 text-lime-700 border-lime-300", C: "bg-amber-50 text-amber-800 border-amber-300",
           D: "bg-orange-50 text-orange-800 border-orange-300", E: "bg-red-50 text-red-700 border-red-300", F: "bg-red-100 text-red-900 border-red-400" }[l]
         || "bg-slate-100 text-slate-600 border-slate-300";
};
const scoreColour = (s) => (s == null ? "#64748b" : s <= 250 ? "#b91c1c" : s <= 450 ? "#c2410c" : s <= 550 ? "#b45309" : s <= 650 ? "#4d7c0f" : "#047857");

function Card({ icon, label, children, testid, right }) {
  return (
    <div className="border rounded-lg bg-white p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">{icon}{label}</p>
        {right}
      </div>
      {children}
    </div>
  );
}

function Delta({ cur, prev }) {
  if (cur == null || prev == null || cur === prev) return <span className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid="cw-score-delta"><Minus className="w-3 h-3" />no change since last pull</span>;
  const up = cur > prev;
  return <span className={`text-xs inline-flex items-center gap-1 ${up ? "text-emerald-700" : "text-red-700"}`} data-testid="cw-score-delta">{up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{up ? "+" : ""}{cur - prev} pts since last pull ({prev})</span>;
}

function ScoreCard({ snap, summary }) {
  const score = snap?.score;
  const pct = score == null ? 0 : Math.min(100, Math.max(0, (score / 850) * 100));
  return (
    <Card icon={<Gauge className="w-3.5 h-3.5" />} label="RiskScore · 0–850" testid="cw-score-card">
      <div className="flex items-end gap-4">
        <div>
          <p className="text-5xl font-semibold leading-none" style={{ color: scoreColour(score) }} data-testid="cw-score-value">{score ?? "—"}</p>
          <p className="text-xs text-muted-foreground mt-1" data-testid="cw-score-band">{snap?.band || "No score yet"}</p>
        </div>
        <div className="flex-1 space-y-1.5 pb-1">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`text-sm px-2 ${ratingCls(snap?.rating)}`} data-testid="cw-rating-chip">{snap?.rating || "—"}</Badge>
            <span className="text-xs text-muted-foreground">credit rating (A1 best → F)</span>
          </div>
          <div className="h-2 w-full rounded bg-slate-100 overflow-hidden"><div className="h-full rounded" style={{ width: `${pct}%`, background: scoreColour(score) }} /></div>
          <Delta cur={score} prev={summary?.prev_score} />
        </div>
      </div>
    </Card>
  );
}

function PredictorCard({ pp }) {
  if (!pp) return <Card icon={<CalendarClock className="w-3.5 h-3.5" />} label="Payment predictor" testid="cw-predictor-card"><p className="text-xs text-muted-foreground">No trade-payment data returned for this entity.</p></Card>;
  const late = Number(pp.entity_days || 0) > Number(pp.industry_days || 0);
  return (
    <Card icon={<CalendarClock className="w-3.5 h-3.5" />} label="Payment predictor · how late they pay suppliers" testid="cw-predictor-card">
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><p className="text-[10px] text-muted-foreground">This business</p><p className={`font-semibold ${late ? "text-red-700" : "text-emerald-700"}`} data-testid="cw-entity-range">{pp.entity_range || "—"}</p><p className="text-xs text-muted-foreground">{pp.entity_days ?? "—"} days predicted</p></div>
        <div><p className="text-[10px] text-muted-foreground">Their industry</p><p className="font-semibold" data-testid="cw-industry-range">{pp.industry_range || "—"}</p><p className="text-xs text-muted-foreground">{pp.industry_days ?? "—"} days predicted</p></div>
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">{pp.trade_lines ?? 0} trade line{pp.trade_lines === 1 ? "" : "s"} reported to CreditorWatch.</p>
    </Card>
  );
}

function TrendChart({ snap, history }) {
  const pts = new Map();
  (snap?.bureau_history || []).forEach((h) => { if (h.score != null) pts.set(h.date, { date: h.date, bureau: h.score, rating: h.rating }); });
  (history || []).forEach((h) => { const d = (h.fetched_at || "").slice(0, 10); if (h.score != null) pts.set(d, { ...(pts.get(d) || { date: d }), portal: h.score, rating: h.rating }); });
  const data = [...pts.values()].sort((a, b) => a.date.localeCompare(b.date));
  if (data.length === 0) return null;
  return (
    <Card icon={<TrendingDown className="w-3.5 h-3.5" />} label={`Score trend · ${data.length} points (bureau 12-month history + every portal pull)`} testid="cw-trend-card">
      <div className="h-48" data-testid="cw-trend-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 6, right: 12, left: -18, bottom: 0 }}>
            <XAxis dataKey="date" tickFormatter={fmtD} tick={{ fontSize: 10 }} minTickGap={24} />
            <YAxis domain={[0, 850]} tick={{ fontSize: 10 }} ticks={[0, 250, 450, 550, 650, 850]} />
            <Tooltip labelFormatter={fmtD} formatter={(v, n, p) => [`${v}${p?.payload?.rating ? ` (${p.payload.rating})` : ""}`, n === "bureau" ? "Bureau history" : "Portal pull"]} contentStyle={{ fontSize: 11 }} />
            <ReferenceLine y={450} stroke="#f59e0b" strokeDasharray="3 3" />
            <ReferenceLine y={250} stroke="#dc2626" strokeDasharray="3 3" />
            <Line type="monotone" dataKey="bureau" stroke="#0f766e" strokeWidth={2} dot={{ r: 2 }} connectNulls isAnimationActive={false} />
            <Line type="monotone" dataKey="portal" stroke="#1d4ed8" strokeWidth={2} dot={{ r: 3 }} connectNulls isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function AlertsCard({ alerts }) {
  return (
    <Card icon={<Bell className="w-3.5 h-3.5" />} label={`Monitoring alerts · ${alerts.length}`} testid="cw-alerts-card">
      {alerts.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="cw-no-alerts">No CreditorWatch alerts received for this ABN yet. Alerts arrive daily after ~4:30pm Sydney once the ABN is on the monitoring list.</p> : (
        <div className="divide-y max-h-64 overflow-y-auto">
          {alerts.map((a) => (
            <div key={a.alert_key} className="py-1.5 text-xs flex items-start gap-2" data-testid={`cw-alert-${a.alert_key.slice(0, 20)}`}>
              <Badge variant="outline" className="text-[10px] shrink-0 bg-rose-50 text-rose-700 border-rose-300">{(a.type || "alert").replace(/_/g, " ")}</Badge>
              <span className="flex-1">{a.issue}</span>
              <span className="text-muted-foreground shrink-0">{fmtD(a.created_date)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

const money = (v) => (v == null || v === "" ? "—" : `$${Number(v).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`);

function AdverseList({ label, rows, render, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">{label} · {rows.length}</p>
      {rows.length === 0 ? <p className="text-xs text-emerald-700">None recorded</p> : <div className="divide-y">{rows.map((r, i) => <p key={i} className="py-1 text-xs">{render(r)}</p>)}</div>}
    </div>
  );
}

function ReportCard({ report, busy, onPull, configured }) {
  const right = <Button size="sm" variant="outline" className="h-6 text-[11px]" disabled={!!busy || !configured} onClick={onPull} data-testid="cw-report-btn">{busy === "report" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileWarning className="w-3 h-3 mr-1" />}{report ? "Refresh full report" : "Pull full report"}</Button>;
  if (!report) return <Card icon={<Scale className="w-3.5 h-3.5" />} label="Adverse data · court actions, defaults, ATO, insolvency" testid="cw-report-card" right={right}><p className="text-xs text-muted-foreground" data-testid="cw-no-report">No full report pulled yet.</p></Card>;
  const bad = report.adverse_total > 0;
  return (
    <Card icon={<Scale className="w-3.5 h-3.5" />} label={`Adverse data · pulled ${fmtDT(report.fetched_at)}`} testid="cw-report-card" right={right}>
      <div className="flex flex-wrap gap-2 mb-3">
        <Badge variant="outline" className={`text-[11px] ${bad ? "bg-red-50 text-red-700 border-red-300" : "bg-emerald-50 text-emerald-700 border-emerald-300"}`} data-testid="cw-adverse-total">{report.adverse_total} adverse item{report.adverse_total === 1 ? "" : "s"}</Badge>
        {report.payment_rating?.rating && <Badge variant="outline" className={`text-[11px] ${ratingCls(report.payment_rating.rating)}`} data-testid="cw-payment-rating">Payment rating {report.payment_rating.rating} · {report.payment_rating.days_overdue ?? 0}d overdue</Badge>}
        {report.ato_tax_default && <Badge variant="outline" className="text-[11px] bg-red-50 text-red-700 border-red-300" data-testid="cw-ato-default">ATO tax default {money(report.ato_tax_default.amount)}</Badge>}
        <Badge variant="outline" className="text-[11px]" data-testid="cw-ppsr-count"><Landmark className="w-3 h-3 mr-1" />{report.ppsr_registrations} PPSR registration{report.ppsr_registrations === 1 ? "" : "s"}</Badge>
        {report.credit_enquiries != null && <Badge variant="outline" className="text-[11px]">{report.credit_enquiries} credit enquiries</Badge>}
        {report.asic_status && <Badge variant="outline" className="text-[11px]">ASIC: {report.asic_status}</Badge>}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <AdverseList label="Court judgements" rows={report.court_judgements} testid="cw-court" render={(r) => <><b>{money(r.amount)}</b> · {r.plaintiff} · {r.claim} · {fmtD(r.date)} {r.court ? `· ${r.court}` : ""}</>} />
        <AdverseList label="Payment defaults" rows={report.payment_defaults} testid="cw-defaults" render={(r) => <><b>{money(r.amount)}</b> · {r.poster} · due {fmtD(r.due)} · {r.settled ? <span className="text-emerald-700">settled</span> : <span className="text-red-700">unsettled</span>}</>} />
        <AdverseList label="Insolvency notices" rows={report.insolvency_notices} testid="cw-insolvency" render={(r) => <>{r.title} · {fmtD(r.date)} · {r.publisher}</>} />
        <AdverseList label="Mercantile enquiries (other collectors)" rows={report.mercantile_enquiries} testid="cw-mercantile" render={(r) => <>{r.agent} · {fmtD(r.date)}</>} />
      </div>
      {report.directors?.length > 0 && (
        <div className="mt-3" data-testid="cw-directors">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1"><Users className="w-3 h-3" />Current directors · {report.directors.length}</p>
          <div className="flex flex-wrap gap-1.5">{report.directors.map((d, i) => <Badge key={i} variant="outline" className={`text-[11px] ${d.high_risk_links ? "bg-red-50 text-red-700 border-red-300" : ""}`}>{d.name}{d.other_directorships ? ` · ${d.other_directorships} other co${d.other_directorships === 1 ? "" : "s"}` : ""}{d.high_risk_links ? ` · ${d.high_risk_links} high-risk` : ""}</Badge>)}</div>
        </div>
      )}
    </Card>
  );
}

function DocsCandidates({ caseId, list, onLinked }) {
  const [busy, setBusy] = useState(null);
  if (!list?.length) return null;
  return (
    <div className="space-y-1.5" data-testid="cw-docs-candidates">
      <p className="text-xs font-semibold">ABNs captured at Credit Approved for this deal:</p>
      {list.map((c) => (
        <button key={c.abn} disabled={!!busy} onClick={async () => { setBusy(c.abn); try { await linkAsicAbr(caseId, { abn: c.abn }); toast.success(`Linked ABN ${fmtAbn(c.abn)}`); onLinked(); } catch (e) { toast.error(errMsg(e, "Link failed")); } finally { setBusy(null); } }}
                className="w-full text-left border rounded p-2 text-xs hover:bg-emerald-50 flex items-center justify-between gap-2" data-testid={`cw-docs-candidate-${c.abn}`}>
          <span><span className="font-medium">{c.name || "—"}</span> <span className="text-muted-foreground">· ABN {fmtAbn(c.abn)} · {c.source}</span></span>
          {busy === c.abn ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Badge variant="outline" className="text-[10px]"><Link2 className="w-3 h-3 mr-1" />Use</Badge>}
        </button>
      ))}
    </div>
  );
}

function NotConfigured({ mode }) {
  return (
    <div className="border rounded-lg bg-white p-4 space-y-2" data-testid="cw-not-configured">
      <p className="text-sm font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4" />CreditorWatch is not connected ({mode})</p>
      <p className="text-xs text-muted-foreground">Ask your CreditorWatch account manager for <b>API access on a Medium/Corporate plan</b> and an <b>API user login</b> (username + password used against <code>POST /login</code>). Confirm the plan includes <b>Credit Score</b> pulls and the <b>Monitoring</b> watchlist + daily notifications. Then enter them under Admin → API Credentials → CreditorWatch and switch the mode to <b>live</b>.</p>
    </div>
  );
}

export default function CreditorWatchTab({ caseRow }) {
  const caseId = caseRow?.case_id;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try { setData(await getCaseCreditorWatch(caseId)); } catch (e) { setErr(errMsg(e, "Could not load CreditorWatch data")); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { const res = await fn(); setData(res); toast.success(ok); } catch (e) { toast.error(errMsg(e, "CreditorWatch action failed")); } finally { setBusy(null); }
  };

  if (!data && !err) return <div className="border rounded-lg bg-white p-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2" data-testid="cw-loading"><Loader2 className="w-4 h-4 animate-spin" />Talking to CreditorWatch…</div>;
  if (err) return <div className="border rounded-lg bg-white p-6 text-sm text-red-700" data-testid="cw-error">{err} <Button size="sm" variant="outline" className="ml-2 h-7" onClick={load}>Retry</Button></div>;
  if (!data.linked) return (
    <div className="border rounded-lg bg-white p-4 space-y-3" data-testid="cw-unlinked">
      <p className="text-sm font-semibold flex items-center gap-2"><Link2 className="w-4 h-4" />No ABN linked to this case</p>
      <DocsCandidates caseId={caseId} list={data.docs_candidates} onLinked={load} />
      <p className="text-xs text-muted-foreground">CreditorWatch is keyed by ABN. {data.docs_candidates?.length ? "Pick one above, or open" : "Open"} the <b>ASIC / ABR</b> tab, link the right entity for <b>{data.customer_name}</b>, then come back here.</p>
    </div>
  );

  const snap = data.snapshot, sum = data.summary || {};
  const watched = !!sum.on_watchlist;
  return (
    <div className="space-y-3" data-testid="cw-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-base font-semibold flex items-center gap-2"><ShieldAlert className="w-4 h-4" />CreditorWatch
            <Badge variant="outline" className={`text-[10px] ${data.mode === "live" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-amber-50 text-amber-700 border-amber-300"}`} data-testid="cw-mode-badge">{data.mode.toUpperCase()}</Badge>
            <Badge variant="outline" className={`text-[10px] ${watched ? "bg-sky-50 text-sky-700 border-sky-300" : "bg-slate-100 text-slate-600 border-slate-300"}`} data-testid="cw-watch-badge">{watched ? "On monitoring list" : "Not monitored"}</Badge>
          </p>
          <p className="text-xs text-muted-foreground">ABN <span className="font-mono">{fmtAbn(data.abn)}</span>{data.abn_source ? <> · from docs data: <b data-testid="cw-abn-source">{data.abn_source}</b></> : ""} · last pulled {fmtDT(snap?.fetched_at)}{snap?.source ? ` (${snap.source})` : ""} · auto re-pull every {data.refresh_days} days</p>
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!!busy || !data.configured} onClick={() => run("refresh", () => refreshCaseCreditorWatch(caseId), "Fresh RiskScore pulled")} data-testid="cw-refresh-btn">
            {busy === "refresh" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}Pull score now
          </Button>
          {data.can_write && (watched
            ? <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-700" disabled={!!busy} onClick={() => run("unwatch", () => unwatchCaseCreditorWatch(caseId), "Removed from monitoring")} data-testid="cw-unwatch-btn"><EyeOff className="w-3.5 h-3.5 mr-1" />Stop monitoring</Button>
            : <Button size="sm" className="h-7 text-xs" disabled={!!busy || !data.configured} onClick={() => run("watch", () => watchCaseCreditorWatch(caseId), "Added to CreditorWatch monitoring")} data-testid="cw-watch-btn">{busy === "watch" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1" />}Add to monitoring</Button>)}
        </div>
      </div>
      {!data.configured && <NotConfigured mode={data.mode} />}
      {data.error && <div className="border border-amber-300 bg-amber-50 rounded-lg p-2 text-xs text-amber-900" data-testid="cw-fetch-error">CreditorWatch: {data.error}</div>}
      <div className="grid md:grid-cols-2 gap-3">
        <ScoreCard snap={snap} summary={sum} />
        <PredictorCard pp={snap?.payment_predictor} />
      </div>
      <TrendChart snap={snap} history={data.history} />
      <ReportCard report={data.report} busy={busy} configured={data.configured} onPull={() => run("report", () => pullCaseCreditorWatchReport(caseId), "Full report pulled")} />
      <AlertsCard alerts={data.alerts || []} />
      <p className="text-[11px] text-muted-foreground">Each "Pull score now" is a billable CreditorWatch credit-score purchase. The nightly monitor (4:30pm Sydney) enrols every open AUS arrears ABN, ingests the day's alerts (ASIC changes, court actions, defaults, rating downgrades) and only re-pulls a score when an alert lands or the last pull is older than {data.refresh_days} days.</p>
    </div>
  );
}
